<template>
  <v-container>
    <v-form :lazy-validation="true">
      <validation-observer ref="validationObserver" v-slot="{ invalid }">
        <v-row class="mb-2">
          <v-col cols="2" v-if="isAdministrator && currentUser.id !== user.id">
            <v-switch
              v-model="user.enabled"
              :label="$t('form.fields.enabled')"
            ></v-switch>
          </v-col>
          <v-col>
            <validation-provider
              :name="usernameLabel"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="usernameLabel + ' *'"
                v-model="user.username"
                required
                outlined
                :error-messages="errors"
                :disabled="!isAdministrator"
              >
              </v-text-field>
            </validation-provider>
          </v-col>
          <v-col v-if="isAdministrator">
            <v-text-field
              label="CSEGN Id"
              v-model="user.attributes.csegnId"
              outlined
            >
            </v-text-field>
          </v-col>
          <v-btn
            class="mt-5 mr-3"
            color="primary"
            @click="changePassword"
            v-if="user.id && currentUser.id !== user.id"
          >
            <v-icon>mdi-key</v-icon>
            {{ $t("form.changePassword") }}
          </v-btn>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col v-if="isUserMember">
            <validation-provider
              :name="$t('form.fields.societySections')"
              v-slot="{ errors }"
              rules="required|societySectionsRequired"
            >
              <v-combobox
                v-model="user.attributes.societySections"
                :items="[1, 2, 3, 4, 5]"
                :label="$t('form.fields.societySections') + ' *'"
                multiple
                outlined
                :error-messages="errors"
              >
                <template v-slot:item="{ item, attrs }">
                  <v-checkbox
                    v-model="attrs.inputValue"
                    :label="$t(`societySection.${item}`)"
                    color="primary"
                  ></v-checkbox>
                </template>
                <template v-slot:selection="{ attrs, item, selected }">
                  <v-chip
                    v-bind="attrs"
                    :input-value="selected"
                    close
                    @click:close="removeSocietySection(item)"
                  >
                    {{ $t(`societySection.${item}`) }}
                  </v-chip>
                </template>
              </v-combobox>
            </validation-provider>
          </v-col>
          <v-col>
            <v-combobox
              v-model="userGroups"
              :items="allGroups"
              :label="$t('form.fields.groups')"
              multiple
              outlined
              :disabled="!isAdministrator"
            >
              <template v-slot:item="{ item, attrs }">
                <v-checkbox
                  v-model="attrs.inputValue"
                  :label="$t(`groups.${item}`)"
                  color="primary"
                ></v-checkbox>
              </template>
              <template v-slot:selection="{ attrs, item, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  :close="isAdministrator"
                  @click:close="removeGroup(item)"
                >
                  {{ $t(`groups.${item}`) }}
                </v-chip>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2" v-if="isUserMember">
          <v-col>
            <validation-provider
              :name="$t('form.fields.membership')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                :label="$t('form.fields.membership') + ' *'"
                outlined
                v-model="user.attributes.memberType"
                :items="userMemberTypes"
                :error-messages="errors"
                :disabled="!isAdministrator"
              ></v-select>
            </validation-provider>
          </v-col>
          <v-col>
            <v-menu
              v-model="showBirthdate"
              :close-on-content-click="false"
              max-width="290"
              rounded="xl"
            >
              <template #activator="{ on, attrs }">
                <validation-provider
                  :name="$t('form.fields.birthdate')"
                  v-slot="{ errors }"
                  rules="required"
                >
                  <v-text-field
                    :label="$t('form.fields.birthdate') + ' *'"
                    :value="
                      $options.filters.formatDate(user.attributes.birthdate)
                    "
                    v-bind="attrs"
                    v-on="on"
                    append-icon="mdi-calendar"
                    :error-messages="errors"
                    outlined
                  />
                </validation-provider>
              </template>
              <v-date-picker
                v-model="user.attributes.birthdate"
                @change="showBirthdate = false"
                locale="de"
                first-day-of-week="1"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.email')"
              v-slot="{ errors }"
              vid="originalEmail"
              rules="required|email|confirmed:@confirmationEmail"
            >
              <v-text-field
                ref="originalMail"
                v-model="user.email"
                :label="$t('form.fields.email') + ' *'"
                required
                outlined
                :error-messages="errors"
              >
              </v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.confirmationEmail')"
              v-slot="{ errors }"
              vid="confirmationEmail"
              rules="required|email|confirmed:@originalEmail"
            >
              <v-text-field
                v-model="confirmationEmail"
                :label="$t('form.fields.confirmationEmail') + ' *'"
                required
                outlined
                :error-messages="errors"
              >
              </v-text-field>
            </validation-provider>
          </v-col>
        </v-row>

        <v-row class="hidden-lg-and-up mt-n8 mb-n6">
          <v-col>
            <v-select
              :label="$t('form.fields.salutation.label')"
              outlined
              v-model="user.attributes.salutation"
              :items="genderTypes"
              v-if="isSupporter"
            ></v-select>
            <validation-provider
              :name="$t('form.fields.salutation.label')"
              v-slot="{ errors }"
              rules="required"
              v-else
            >
              <v-select
                :label="$t('form.fields.salutation.label') + ' *'"
                outlined
                v-model="user.attributes.salutation"
                :items="genderTypes"
                :error-messages="errors"
              ></v-select>
            </validation-provider>
          </v-col>
          <v-col>
            <v-text-field
              outlined
              v-model="user.attributes.title"
              :label="$t('form.fields.title')"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2">
          <v-col cols="2" class="hidden-md-and-down">
            <v-select
              :label="$t('form.fields.salutation.label')"
              outlined
              v-model="user.attributes.salutation"
              :items="genderTypes"
              v-if="isSupporter"
            ></v-select>
            <validation-provider
              :name="$t('form.fields.salutation.label')"
              v-slot="{ errors }"
              rules="required"
              v-else
            >
              <v-select
                :label="$t('form.fields.salutation.label') + ' *'"
                outlined
                v-model="user.attributes.salutation"
                :items="genderTypes"
                :error-messages="errors"
              ></v-select>
            </validation-provider>
          </v-col>
          <v-col>
            <v-text-field
              :label="$t('form.fields.firstname')"
              outlined
              v-model="user.firstName"
              v-if="isSupporter"
            ></v-text-field>
            <validation-provider
              :name="$t('form.fields.firstname')"
              v-slot="{ errors }"
              rules="required"
              v-else
            >
              <v-text-field
                :label="$t('form.fields.firstname') + ' *'"
                outlined
                v-model="user.firstName"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col>
            <v-text-field
              :label="$t('form.fields.lastname')"
              outlined
              v-model="user.lastName"
              v-if="isSupporter"
            ></v-text-field>
            <validation-provider
              :name="$t('form.fields.lastname')"
              v-slot="{ errors }"
              rules="required"
              v-else
            >
              <v-text-field
                :label="$t('form.fields.lastname') + ' *'"
                outlined
                v-model="user.lastName"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="2" class="hidden-md-and-down">
            <v-text-field
              outlined
              v-model="user.attributes.title"
              :label="$t('form.fields.title')"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2" v-if="!isSupporter">
          <v-col>
            <v-switch
              v-model="user.attributes.hasCompanyAddress"
              :label="$t('form.fields.companyAddress')"
            ></v-switch>
          </v-col>
        </v-row>
        <v-row
          class="mt-n8 mb-2"
          v-if="user.attributes.hasCompanyAddress || isSupporter"
        >
          <v-col>
            <validation-provider
              :name="$t('form.fields.company')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.company') + ' *'"
                outlined
                v-model="user.attributes.company"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col>
            <v-text-field
              :label="$t('form.fields.department')"
              outlined
              v-model="user.attributes.department"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row
          class="mt-n8 mb-2"
          v-if="user.attributes.hasCompanyAddress || isSupporter"
        >
          <v-col>
            <v-text-field
              :label="$t('form.fields.companyAddition')"
              outlined
              v-model="user.attributes.companyAddition"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2">
          <v-col>
            <validation-provider
              :name="$t('form.fields.street')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.street') + ' *'"
                outlined
                v-model="user.attributes.street"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="2">
            <validation-provider
              :name="$t('form.fields.zip')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.zip') + ' *'"
                outlined
                v-model="user.attributes.zip"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="3">
            <validation-provider
              :name="$t('form.fields.city')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                :label="$t('form.fields.city') + ' *'"
                outlined
                v-model="user.attributes.city"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <validation-provider
              :name="$t('form.fields.country')"
              v-slot="{ errors }"
              rules="required"
            >
              <v-autocomplete
                :label="$t('form.fields.country') + ' *'"
                outlined
                :items="countries"
                v-model="user.attributes.country"
                item-text="value"
                item-value="key"
                :error-messages="errors"
              >
              </v-autocomplete>
            </validation-provider>
          </v-col>
          <v-col cols="6">
            <v-text-field
              label="UID"
              outlined
              v-model="user.attributes.uid"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2">
          <v-col cols="6">
            <v-text-field
              :label="$t('form.fields.phone')"
              outlined
              v-model="user.attributes.telephone"
              type="tel"
            ></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              :label="$t('form.fields.mobilePhone')"
              outlined
              v-model="user.attributes.cellphone"
              type="tel"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-n8 mb-2">
          <v-col>
            <v-switch
              v-model="user.attributes.hasInvoiceAddress"
              :label="$t('form.fields.differentInvoiceAddress')"
            ></v-switch>
          </v-col>
        </v-row>
        <v-expansion-panels
          :disabled="!user.attributes.hasInvoiceAddress"
          :value="user.attributes.hasInvoiceAddress ? 0 : null"
          class="mt-n8 mb-2"
        >
          <v-expansion-panel>
            <v-expansion-panel-header>
              {{ $t("form.fields.invoiceAddress") }}
            </v-expansion-panel-header>
            <v-expansion-panel-content v-if="user.attributes.hasInvoiceAddress">
              <v-row class="hidden-lg-and-up mb-n6">
                <v-col>
                  <v-select
                    :label="$t('form.fields.salutation.label')"
                    outlined
                    v-model="user.attributes.invoiceSalutation"
                    :items="genderTypes"
                  ></v-select>
                </v-col>
                <v-col>
                  <v-text-field
                    outlined
                    v-model="user.attributes.invoiceTitle"
                    :label="$t('form.fields.title')"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row class="mb-2">
                <v-col cols="2" class="hidden-md-and-down">
                  <v-select
                    :label="$t('form.fields.salutation.label')"
                    outlined
                    v-model="user.attributes.invoiceSalutation"
                    :items="genderTypes"
                  ></v-select>
                </v-col>
                <v-col>
                  <v-text-field
                    :label="$t('form.fields.firstname')"
                    outlined
                    v-model="user.attributes.invoiceFirstName"
                  ></v-text-field>
                </v-col>
                <v-col>
                  <v-text-field
                    :label="$t('form.fields.lastname')"
                    outlined
                    v-model="user.attributes.invoiceLastName"
                  ></v-text-field>
                </v-col>
                <v-col cols="2" class="hidden-md-and-down">
                  <v-text-field
                    outlined
                    v-model="user.attributes.invoiceTitle"
                    :label="$t('form.fields.title')"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row class="mt-n8 mb-2">
                <v-col>
                  <validation-provider
                    :name="$t('form.fields.street')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :label="$t('form.fields.street') + ' *'"
                      outlined
                      v-model="user.attributes.invoiceStreet"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="2">
                  <validation-provider
                    :name="$t('form.fields.zip')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :label="$t('form.fields.zip') + ' *'"
                      outlined
                      v-model="user.attributes.invoiceZip"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="3">
                  <validation-provider
                    :name="$t('form.fields.city')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      :label="$t('form.fields.city') + ' *'"
                      outlined
                      v-model="user.attributes.invoiceCity"
                      :error-messages="errors"
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="mt-n8 mb-2">
                  <validation-provider
                    :name="$t('form.fields.country')"
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-autocomplete
                      :label="$t('form.fields.country') + ' *'"
                      outlined
                      :items="countries"
                      v-model="user.attributes.invoiceCountry"
                      item-text="value"
                      item-value="key"
                      :error-messages="errors"
                    >
                    </v-autocomplete>
                  </validation-provider>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row class="mb-n8" v-if="errorMessage">
          <v-col>
            <v-alert dense outlined type="error" v-html="errorMessage">
            </v-alert>
          </v-col>
        </v-row>
        <v-card-actions class="mt-3 mb-n2">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="user.id ? !(hasNewData || hasNewUserGroups) : invalid"
            @click="submit"
          >
            {{ $t("form.btn.save") }}
          </v-btn>
        </v-card-actions>
      </validation-observer>
    </v-form>
    <v-dialog v-model="passwordDialog" width="500">
      <v-card>
        <v-card-title class="white--text headline primary" primary-title>
          {{ $t("form.changePassword") }}
        </v-card-title>
        <v-card-text
          class="pa-md-6"
          v-html="
            $t('form.passwordDialog.text', {
              mail: resetMail,
              password: resetPassword,
            })
          "
        >
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="passwordDialog = false">
            {{ $t("form.btn.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { usersResource } from "../api/resources";
import {
  GenderTypes,
  SocietySections,
  SelectionUserGroups,
  UserMemberTypes,
} from "../config/constants";
import de from "../i18n/de.json";
import { extend } from "vee-validate";

extend("societySectionsRequired", {
  message: () =>
    (window.location.pathname.startsWith("/en") ? en : de)
      .validationYoungMembers,
  validate: (value) => {
    if (value.includes(5) && value.length < 2) {
      return false;
    }
    return true;
  },
});

export default {
  props: ["user", "createUser"],
  data: () => ({
    resetMail: null,
    resetPassword: null,
    confirmationEmail: null,
    initialValues: null,
    passwordDialog: false,
    initialGroupValues: null,
    userGroups: [],
    errorMessage: null,
    allGroups: SelectionUserGroups,
    showBirthdate: false,
  }),
  created() {
    if (!this.createdUser) {
      return;
    }
    this.confirmationEmail = "";
    this.$refs.validationObserver.reset();
  },
  watch: {
    user: {
      immediate: true,
      async handler(value) {
        if (!this.user.id) {
          return;
        }
        this.userGroups = value.id
          ? (
              await usersResource.get(`${value.id}/groups`, {
                headers: {
                  "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
                },
              })
            ).data.filter((group) => group !== "unassigned")
          : [];
        this.initialGroupValues = JSON.stringify(this.userGroups);
        this.confirmationEmail = this.user.email;
        this.resetMail = this.user.email;
        this.initialValues = JSON.stringify(this.user);
      },
    },
  },
  methods: {
    async changePassword() {
      const response = (
        await usersResource.put(
          `${this.user.id}/changePassword`,
          {},
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        )
      ).data;
      this.resetPassword = response.password;
      this.passwordDialog = true;
    },
    async submit() {
      this.errorMessage = null;
      let createdUser = false;
      // create new user
      if (!this.user.id) {
        createdUser = true;
        await usersResource
          .post("", this.user, {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
          .then((response) => {
            this.user.id = response.data.id;
          })
          .catch((error) => {
            const errorMessage = error.response.data.errorMessage;
            if (errorMessage && errorMessage.includes("username")) {
              this.errorMessage = this.$t("error.usernameExists", {
                username: this.user.username,
              });
            } else {
              this.errorMessage = this.$t("error.mailExists", {
                mail: this.user.email,
              });
            }
          });
        if (this.errorMessage) {
          return;
        }
      } else if (this.hasNewData) {
        const oldUserData = JSON.parse(this.initialValues);
        const mailData = { attributes: {} };
        for (let key in this.user) {
          if (key !== "attributes") {
            mailData[key] = {
              value: this.user[key],
              hasNewValue: this.user[key] !== oldUserData[key],
            };
          } else {
            for (let attributeKey in this.user.attributes) {
              mailData.attributes[attributeKey] = {
                value: this.user.attributes[attributeKey],
                hasNewValue:
                  this.user.attributes[attributeKey] !==
                  oldUserData.attributes[attributeKey],
              };
            }
          }
        }
        mailData.isMember = this.isUserMember;
        await usersResource
          .put(this.user.id, this.user, {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
          .catch((error) => {
            const errorMessage = error.response.data.errorMessage;
            if (errorMessage && errorMessage.includes("username")) {
              this.errorMessage = this.$t("error.usernameExists", {
                username: this.user.username,
              });
            } else {
              this.errorMessage = this.$t("error.mailExists", {
                mail: this.user.email,
              });
            }
          });
        if (this.errorMessage) {
          return;
        }
        await usersResource.post(
          "update/mail",
          { user: this.user, mailData: mailData },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        );
        this.resetMail = this.user.email;
        this.initialValues = JSON.stringify(this.user);
      }
      if (createdUser || this.hasNewUserGroups) {
        if (
          this.isAdministrator &&
          this.currentUser.id === this.user.id &&
          this.userGroups.indexOf("administrator") < 0
        ) {
          this.userGroups.push("administrator");
        }
        await usersResource.put(`${this.user.id}/groups`, this.userGroups, {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        });
        this.initialGroupValues = JSON.stringify(this.userGroups);
      }
      this.$refs.validationObserver.reset();
      this.$emit("userSave");
    },
    removeGroup(item) {
      this.userGroups.splice(this.userGroups.indexOf(item), 1);
    },
    removeSocietySection(item) {
      this.user.attributes.societySections.splice(
        this.user.attributes.societySections.indexOf(item),
        1
      );
    },
  },
  computed: {
    ...mapGetters(["currentUser", "isAdministrator"]),
    societySections() {
      return SocietySections.map((t) => {
        return { value: "" + t, text: this.$t(`societySection.${t}`) };
      });
    },
    genderTypes() {
      return GenderTypes.map((t) => {
        return { value: t, text: this.$t(`form.fields.salutation.${t}`) };
      });
    },
    allGroupValues() {
      return this.allGroups.map((group) => ({
        value: group,
        text: this.$t(`groups.${group}`),
      }));
    },
    userGroupValues() {
      return this.userGroups.map((group) => ({
        value: group,
        text: this.$t(`groups.${group}`),
      }));
    },
    countries() {
      return Object.keys(de.countries).map((countryKey) => {
        return {
          key: countryKey,
          value: this.$t(`countries.${countryKey}`),
        };
      });
    },
    userMemberTypes() {
      return UserMemberTypes.map((t) => {
        return { value: t, text: this.$t(`memberType.${t}`) };
      });
    },
    hasNewData() {
      return (
        this.confirmationEmail &&
        this.confirmationEmail === this.user.email &&
        JSON.stringify(this.user) !== this.initialValues
      );
    },
    hasNewUserGroups() {
      return JSON.stringify(this.userGroups) !== this.initialGroupValues;
    },
    isSupporter() {
      return this.user.attributes.memberType === "F";
    },
    isUserMember() {
      return this.userGroups.indexOf("member") >= 0;
    },
    usernameLabel() {
      return this.isUserMember
        ? this.$t("form.fields.membershipNumber")
        : this.$t("form.fields.username");
    },
  },
};
</script>
